import React from 'react';

import type { PlayerGraphResultsDto } from '@poker-stats/contracts';

import formatWinnings from '../../utils/formatWinnings';

type V2RealChartProps = {
  graph?: PlayerGraphResultsDto;

  loading?: boolean;

  winningsLabel?: string;

  evLabel?: string;

  handsLabel?: string;
};

function buildSeries(graph?: PlayerGraphResultsDto) {
  if (!graph?.result?.length) {
    return null;
  }

  const cumulative = graph.result.reduce<
    Array<{ total: number; showdown: number; nonShowdown: number; ev: number }>
  >(
    (acc, _, index) => {
      const prev = acc[acc.length - 1];

      acc.push({
        total: prev.total + graph.result[index],

        showdown: prev.showdown + graph.resultWithShowdown[index],

        nonShowdown: prev.nonShowdown + graph.resultWithoutShowdown[index],

        ev: prev.ev + (graph.resultEv?.[index] ?? 0),
      });

      return acc;
    },

    [{ total: 0, showdown: 0, nonShowdown: 0, ev: 0 }],
  );

  return cumulative.slice(1);
}

function polyline(values: number[], color: string, min: number, max: number, dash = '') {
  if (values.length === 0) {
    return null;
  }

  const range = max - min || 1;

  const points = values

    .map((value, index) => {
      const x = ((index * 1000) / Math.max(values.length - 1, 1)).toFixed(1);

      const y = (189 - ((value - min) / range) * 147).toFixed(1);

      return `${x},${y}`;
    })

    .join(' ');

  return (
    <polyline
      points={points}
      fill="none"
      stroke={color}
      strokeWidth="2"
      vectorEffect="non-scaling-stroke"
      strokeDasharray={dash || undefined}
    />
  );
}

const V2RealChart: React.FC<V2RealChartProps> = ({
  graph,

  loading,

  winningsLabel,

  evLabel,

  handsLabel,
}) => {
  const series = buildSeries(graph);

  if (loading) {
    return <p className="chart-caption">Загрузка графика…</p>;
  }

  if (!series) {
    return <p className="chart-caption">Нет данных для графика.</p>;
  }

  const totals = series.map((point) => point.total);

  const showdowns = series.map((point) => point.showdown);

  const nonShowdowns = series.map((point) => point.nonShowdown);

  const evSeries = series.map((point) => point.ev);

  const hasEv = (graph?.resultEv?.length ?? 0) > 0;

  const allValues = hasEv
    ? [...totals, ...showdowns, ...nonShowdowns, ...evSeries]
    : [...totals, ...showdowns, ...nonShowdowns];

  const min = Math.min(...allValues, 0);

  const max = Math.max(...allValues, 0);

  const finalTotal = totals[totals.length - 1] ?? 0;

  const finalEv = evSeries[evSeries.length - 1] ?? 0;

  const resolvedEvLabel = evLabel ?? formatWinnings(finalEv, 1, true);

  return (
    <>
      <svg
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        viewBox="0 0 1000 225"
        preserveAspectRatio="none"
        role="img"
        aria-label="Динамика результатов"
      >
        {[42, 91, 140, 189].map((y) => (
          <path
            key={y}
            d={`M0 ${y}H1000`}
            stroke="var(--line)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
            strokeDasharray="2 5"
          />
        ))}

        {polyline(nonShowdowns, 'var(--chart-non-showdown)', min, max, '4 5')}

        {polyline(showdowns, 'var(--chart-showdown)', min, max)}

        {hasEv ? polyline(evSeries, 'var(--chart-ev)', min, max, '6 4') : null}

        {polyline(totals, 'var(--chart-profit)', min, max)}
      </svg>

      <div className="chart-y-axis">
        <span>{formatWinnings(max, 0, true)}</span>

        <span>{formatWinnings((max + min) / 2, 0, true)}</span>

        <span>{formatWinnings(0, 0, true)}</span>

        <span>{formatWinnings(min, 0, true)}</span>
      </div>

      <div className="chart-tooltip">
        {handsLabel ?? '—'} &nbsp;{' '}
        <strong className={finalTotal >= 0 ? 'positive' : 'negative'}>
          {winningsLabel ?? formatWinnings(finalTotal, 1, true)}
        </strong>
        {hasEv ? (
          <>
            {' '}
            · EV{' '}
            <strong className={finalEv >= 0 ? 'positive' : 'negative'}>{resolvedEvLabel}</strong>
          </>
        ) : null}
      </div>
    </>
  );
};

export default V2RealChart;
